import { useCallback, useEffect, useMemo, useState, type ChangeEvent, type FormEvent } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import {
  ApiError,
  leaders as leadersApi,
  website as websiteApi,
  type LeaderRow,
  type SocialLink,
  type UnitWebsiteInfo,
} from '../api';
import { useAuth } from '../auth';
import { useUnits } from '../units';

const STATUS_LABELS: Record<UnitWebsiteInfo['status'], string> = {
  draft: 'Not published',
  published: 'Published',
  deactivated: 'Deactivated',
};

export function Website() {
  const { unitId = '' } = useParams();
  const { me } = useAuth();
  const { units } = useUnits();

  const mayAdminister = useMemo(
    () => me?.isPlatformAdmin || units.find((u) => u.id === unitId)?.roles.includes('ADMIN'),
    [me, units, unitId],
  );

  const [info, setInfo] = useState<UnitWebsiteInfo | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    websiteApi
      .get(unitId)
      .then(setInfo)
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Failed to load website settings'));
  }, [unitId]);

  useEffect(() => {
    if (mayAdminister) load();
  }, [mayAdminister, load]);

  if (!mayAdminister) return <Navigate to="/" replace />;

  return (
    <div className="panel">
      <h1>Unit website</h1>
      <p className="muted">
        Branding for this Unit's public website.{' '}
        {info && (
          <strong>
            Status: {STATUS_LABELS[info.status]}
            {info.subdomain ? ` · ${info.subdomain}` : ''}
          </strong>
        )}
      </p>
      {error && <p className="error">{error}</p>}
      {info && <PublishPanel unitId={unitId} info={info} onChanged={setInfo} />}
      {info && <BrandingForm unitId={unitId} initial={info} onSaved={setInfo} />}
      <LeadersPanel unitId={unitId} />
    </div>
  );
}

function LeadersPanel({ unitId }: { unitId: string }) {
  const [rows, setRows] = useState<LeaderRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    leadersApi
      .list(unitId)
      .then(setRows)
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Failed to load leaders'));
  }, [unitId]);

  useEffect(load, [load]);

  async function move(leaderId: string, direction: 'up' | 'down') {
    try {
      setRows(await leadersApi.move(unitId, leaderId, direction));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not reorder');
    }
  }

  async function remove(leaderId: string, name: string) {
    if (!confirm(`Remove ${name} from the Leaders page?`)) return;
    try {
      await leadersApi.remove(unitId, leaderId);
      load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not remove leader');
    }
  }

  return (
    <section>
      <h2>Leaders</h2>
      <p className="muted small">Shown on the public site's Leaders page, in this order.</p>
      {error && <p className="error">{error}</p>}
      {!rows ? (
        <p className="muted">Loading…</p>
      ) : (
        rows.map((leader, i) =>
          editingId === leader.id ? (
            <LeaderForm
              key={leader.id}
              unitId={unitId}
              existing={leader}
              onSaved={() => {
                setEditingId(null);
                load();
              }}
              onCancel={() => setEditingId(null)}
            />
          ) : (
            <div className="req-block" key={leader.id}>
              <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                <strong>
                  {leader.name} <span className="muted small">— {leader.title}</span>
                </strong>
                <div className="row" style={{ gap: 8 }}>
                  <button className="linklike" onClick={() => move(leader.id, 'up')} disabled={i === 0}>
                    Move up
                  </button>
                  <button
                    className="linklike"
                    onClick={() => move(leader.id, 'down')}
                    disabled={i === rows.length - 1}
                  >
                    Move down
                  </button>
                  <button className="linklike" onClick={() => setEditingId(leader.id)}>
                    Edit
                  </button>
                  <button className="linklike" onClick={() => remove(leader.id, leader.name)}>
                    Remove
                  </button>
                </div>
              </div>
              {leader.bio && <p className="muted small">{leader.bio}</p>}
            </div>
          ),
        )
      )}
      {adding ? (
        <LeaderForm
          unitId={unitId}
          onSaved={() => {
            setAdding(false);
            load();
          }}
          onCancel={() => setAdding(false)}
        />
      ) : (
        <button onClick={() => setAdding(true)}>Add leader</button>
      )}
    </section>
  );
}

function LeaderForm({
  unitId,
  existing,
  onSaved,
  onCancel,
}: {
  unitId: string;
  existing?: LeaderRow;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(existing?.name ?? '');
  const [title, setTitle] = useState(existing?.title ?? '');
  const [photoUrl, setPhotoUrl] = useState(existing?.photoUrl ?? '');
  const [bio, setBio] = useState(existing?.bio ?? '');
  const [contactEmail, setContactEmail] = useState(existing?.contactEmail ?? '');
  const [contactPhone, setContactPhone] = useState(existing?.contactPhone ?? '');
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onPhotoSelected(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // lets picking the same file again re-fire onChange
    if (!file) return;
    setError(null);
    setUploading(true);
    try {
      const { url } = await leadersApi.uploadPhoto(unitId, file);
      setPhotoUrl(url);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not upload photo');
    } finally {
      setUploading(false);
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const payload = {
        name,
        title,
        photoUrl: photoUrl.trim() || null,
        bio: bio.trim() || null,
        contactEmail: contactEmail.trim() || null,
        contactPhone: contactPhone.trim() || null,
      };
      if (existing) await leadersApi.update(unitId, existing.id, payload);
      else await leadersApi.create(unitId, payload);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save leader');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="event-form" onSubmit={submit}>
      <input placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} required />
      <input
        placeholder="Title (e.g. Cubmaster)"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        required
      />
      <div className="row" style={{ gap: 8, alignItems: 'center' }}>
        {photoUrl && (
          <img
            src={photoUrl}
            alt=""
            style={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover' }}
          />
        )}
        <label>
          {uploading ? 'Uploading…' : photoUrl ? 'Replace photo' : 'Upload photo'}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={onPhotoSelected}
            disabled={uploading}
            style={{ display: 'block' }}
          />
        </label>
        {photoUrl && !uploading && (
          <button type="button" className="linklike" onClick={() => setPhotoUrl('')}>
            Remove photo
          </button>
        )}
      </div>
      <textarea rows={3} placeholder="Bio" value={bio} onChange={(e) => setBio(e.target.value)} />
      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
        <input
          type="email"
          placeholder="Contact email"
          value={contactEmail}
          onChange={(e) => setContactEmail(e.target.value)}
        />
        <input
          type="tel"
          placeholder="Contact phone"
          value={contactPhone}
          onChange={(e) => setContactPhone(e.target.value)}
        />
      </div>
      <div className="row" style={{ gap: 8 }}>
        <button type="submit" className="primary" disabled={busy || uploading || !name || !title}>
          {existing ? 'Save leader' : 'Add leader'}
        </button>
        <button type="button" onClick={onCancel}>
          Cancel
        </button>
        {error && <span className="error">{error}</span>}
      </div>
    </form>
  );
}

function PublishPanel({
  unitId,
  info,
  onChanged,
}: {
  unitId: string;
  info: UnitWebsiteInfo;
  onChanged: (info: UnitWebsiteInfo) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function publish() {
    setError(null);
    setBusy(true);
    try {
      onChanged(await websiteApi.publish(unitId));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not publish');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="callout" style={{ marginBottom: 16 }}>
      <div className="row" style={{ alignItems: 'center' }}>
        <div>
          {info.url ? (
            <p style={{ margin: 0 }}>
              Live at <a href={info.url} target="_blank" rel="noreferrer">{info.url}</a>
            </p>
          ) : (
            <p className="muted" style={{ margin: 0 }}>
              Not published yet — publishing allocates a subdomain and pushes branding,
              upcoming events, and public announcements.
            </p>
          )}
          <p className="muted small" style={{ margin: '4px 0 0' }}>
            {info.lastSyncedAt
              ? `Last synced ${new Date(info.lastSyncedAt).toLocaleString()}`
              : info.status === 'published'
                ? 'Sync pending…'
                : null}
          </p>
          {info.lastSyncError && <p className="error small" style={{ margin: '4px 0 0' }}>Sync error: {info.lastSyncError}</p>}
        </div>
        <button className="primary" onClick={publish} disabled={busy}>
          {busy ? 'Working…' : info.status === 'published' ? 'Resync now' : 'Publish website'}
        </button>
      </div>
      {error && <p className="error">{error}</p>}
    </div>
  );
}

function BrandingForm({
  unitId,
  initial,
  onSaved,
}: {
  unitId: string;
  initial: UnitWebsiteInfo;
  onSaved: (info: UnitWebsiteInfo) => void;
}) {
  const [displayName, setDisplayName] = useState(initial.branding.displayName);
  const [logoUrl, setLogoUrl] = useState(initial.branding.logoUrl ?? '');
  const [primaryColor, setPrimaryColor] = useState(initial.branding.primaryColor ?? '');
  const [accentColor, setAccentColor] = useState(initial.branding.accentColor ?? '');
  const [socialLinks, setSocialLinks] = useState<SocialLink[]>(initial.branding.socialLinks);
  const [contactEmail, setContactEmail] = useState(initial.branding.contactEmail ?? '');
  const [contactPhone, setContactPhone] = useState(initial.branding.contactPhone ?? '');
  const [charterOrg, setCharterOrg] = useState(initial.branding.charterOrg ?? '');
  const [city, setCity] = useState(initial.branding.city ?? '');
  const [state, setState] = useState(initial.branding.state ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSaved(false);
    setBusy(true);
    try {
      const info = await websiteApi.updateBranding(unitId, {
        displayName: displayName.trim(),
        logoUrl: logoUrl.trim() || null,
        primaryColor: primaryColor.trim() || null,
        accentColor: accentColor.trim() || null,
        socialLinks: socialLinks.filter((l) => l.platform.trim() && l.url.trim()),
        contactEmail: contactEmail.trim() || null,
        contactPhone: contactPhone.trim() || null,
        charterOrg: charterOrg.trim() || null,
        city: city.trim() || null,
        state: state.trim() || null,
      });
      onSaved(info);
      setSaved(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save branding');
    } finally {
      setBusy(false);
    }
  }

  function updateLink(i: number, patch: Partial<SocialLink>) {
    setSocialLinks((links) => links.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  }

  return (
    <form className="settings-form" onSubmit={submit}>
      <label>
        Display name
        <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} required />
      </label>
      <label>
        Logo URL
        <input
          type="url"
          placeholder="https://example.com/logo.png"
          value={logoUrl}
          onChange={(e) => setLogoUrl(e.target.value)}
        />
      </label>
      <label>
        Primary color
        <input
          placeholder="#1a2b3c"
          value={primaryColor}
          onChange={(e) => setPrimaryColor(e.target.value)}
        />
      </label>
      <label>
        Accent color
        <input
          placeholder="#1a2b3c"
          value={accentColor}
          onChange={(e) => setAccentColor(e.target.value)}
        />
      </label>

      <label>
        Contact email
        <input
          type="email"
          placeholder="pack9@example.com"
          value={contactEmail}
          onChange={(e) => setContactEmail(e.target.value)}
        />
      </label>
      <label>
        Contact phone
        <input
          type="tel"
          placeholder="(555) 555-0100"
          value={contactPhone}
          onChange={(e) => setContactPhone(e.target.value)}
        />
      </label>

      <label>
        Charter organization
        <input
          placeholder="First Baptist Church"
          value={charterOrg}
          onChange={(e) => setCharterOrg(e.target.value)}
        />
      </label>
      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
        <label>
          City
          <input placeholder="Anytown" value={city} onChange={(e) => setCity(e.target.value)} />
        </label>
        <label>
          State
          <input placeholder="CA" value={state} onChange={(e) => setState(e.target.value)} />
        </label>
      </div>

      <h2>Social links</h2>
      {socialLinks.map((link, i) => (
        <div className="row" key={i}>
          <input
            placeholder="platform (e.g. facebook)"
            value={link.platform}
            onChange={(e) => updateLink(i, { platform: e.target.value })}
          />
          <input
            placeholder="https://…"
            value={link.url}
            onChange={(e) => updateLink(i, { url: e.target.value })}
          />
          <button type="button" onClick={() => setSocialLinks((l) => l.filter((_, idx) => idx !== i))}>
            Remove
          </button>
        </div>
      ))}
      <button type="button" onClick={() => setSocialLinks((l) => [...l, { platform: '', url: '' }])}>
        Add social link
      </button>

      <div className="row">
        <button type="submit" className="primary" disabled={busy}>
          {busy ? 'Saving…' : 'Save branding'}
        </button>
        {saved && <span className="muted">Saved.</span>}
        {error && <span className="error">{error}</span>}
      </div>
    </form>
  );
}
